/**
 * Task Comments API Integration
 */

import axiosClient from '../axiosClient';
import {
  TaskComment,
  CreateCommentDto,
  UpdateCommentDto,
  CommentCountResponse,
} from '../types/CommentTypes';

export const commentsApi = {
  /**
   * Create a comment on a task
   */
  createComment: async (
    taskId: string,
    data: CreateCommentDto,
  ): Promise<TaskComment> => {
    try {
      const response = await axiosClient.post(
        `/tasks/${taskId}/comments`,
        data,
      );
      console.log('✅ Comment created on task:', taskId);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating comment:', error);
      throw error;
    }
  },

  /**
   * Get all comments for a task
   */
  getTaskComments: async (taskId: string): Promise<TaskComment[]> => {
    try {
      const response = await axiosClient.get(`/tasks/${taskId}/comments`);
      console.log('✅ Task comments fetched:', taskId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching task comments:', error);
      throw error;
    }
  },

  /**
   * Get comment count for a task
   */
  getCommentCount: async (taskId: string): Promise<number> => {
    try {
      const response = await axiosClient.get<CommentCountResponse>(
        `/tasks/${taskId}/comments/count`,
      );
      console.log('✅ Comment count fetched:', taskId);
      return response.data.count;
    } catch (error) {
      console.error('❌ Error fetching comment count:', error);
      throw error;
    }
  },

  /**
   * Update a comment
   */
  updateComment: async (
    taskId: string,
    commentId: string,
    data: UpdateCommentDto,
  ): Promise<TaskComment> => {
    try {
      const response = await axiosClient.put(
        `/tasks/${taskId}/comments/${commentId}`,
        data,
      );
      console.log('✅ Comment updated:', commentId);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating comment:', error);
      throw error;
    }
  },

  /**
   * Delete a comment
   */
  deleteComment: async (taskId: string, commentId: string): Promise<void> => {
    try {
      await axiosClient.delete(`/tasks/${taskId}/comments/${commentId}`);
      console.log('✅ Comment deleted:', commentId);
    } catch (error) {
      console.error('❌ Error deleting comment:', error);
      throw error;
    }
  },

  /**
   * Get replies to a comment
   */
  getCommentReplies: async (
    taskId: string,
    commentId: string,
  ): Promise<TaskComment[]> => {
    try {
      const response = await axiosClient.get(
        `/tasks/${taskId}/comments/${commentId}/replies`,
      );
      console.log('✅ Comment replies fetched:', commentId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching comment replies:', error);
      throw error;
    }
  },
};

export default commentsApi;
